import { isValid, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { redirect } from 'react-router-dom';
import { stripTimezoneFromIsoDate } from 'platform/utilities/date/index';
import {
  TOGGLE_NAMES,
  toggleValuesSelector,
} from 'platform/utilities/feature-toggles/useFeatureToggle';
import {
  CLAIMANT_ID_PATTERN,
  SEARCH_PARAMS,
  SORT_BY,
  SUBMISSION_DEFAULTS,
} from './constants';
import { NetworkError } from './api';
import { waitForToggles } from './quickSubmit';

/**
 * The two sources a row can come from, as the table labels them. "Online
 * form" is the portal's term for a form submitted through it, as opposed to
 * a QuickSubmit upload. The design spells QuickSubmit as one word.
 */
export const SUBMISSION_TYPES = Object.freeze({
  ONLINE_FORM: 'Online form',
  QUICK_SUBMIT: 'QuickSubmit',
});

/**
 * One combined history row, as `GET /quick_submit/v0/arp/submission_history`
 * returns it, plus `type`, which the loader derives from `source`. Only the
 * keys the history reads are listed. Every key is on every row, null where
 * the source has no equivalent. An online form has a `formType`. A
 * QuickSubmit upload has `packetType`, `packetStatus`, and `numberOfFiles`
 * instead.
 *
 * @typedef {Object} CombinedHistorySubmission
 * @property {string} id The source's own ID prefixed by `source`, such as
 *   `arp_form-<uuid>` or `qs-101310`.
 * @property {'arp_form'|'arp'|'qs'|'myva'} source `arp_form` for a form
 *   submitted through the portal, otherwise the channel the QuickSubmit
 *   packet came through.
 * @property {string} type One of `SUBMISSION_TYPES`, derived from `source`.
 * @property {string|null} claimantId Null for an upload with no ICN.
 * @property {string|null} firstName Null when the source gave only one name.
 * @property {string|null} lastName
 * @property {string|null} benefitType Null for an upload.
 * @property {string|null} formType Null for an upload.
 * @property {boolean} packet False for an upload.
 * @property {string|null} confirmationNumber Null for an upload with no
 *   confirmation number.
 * @property {string|null} submittedDate An ISO 8601 date for a form, or a
 *   datetime without a zone for an upload. Null for an upload with no upload
 *   date.
 * @property {'received'|'awaiting_receipt'|'awaiting_receipt_warning'|'processing_error'|null} vbmsStatus
 *   Null for a QuickSubmit `packetStatus` with no VBMS equivalent.
 * @property {string|null} vbmsReceivedDate When a received row was received:
 *   an ISO 8601 date for a form, or the datetime without a zone at which
 *   QuickSubmit completed an upload. Null for every other row.
 * @property {string|null} packetType Upload only.
 * @property {string|null} packetStatus Upload only, as QuickSubmit reports
 *   it.
 * @property {number|null} numberOfFiles Upload only.
 * @property {string|null} maskedIdentifier The claimant's SSN or VA file
 *   number as vets-api masks it, five asterisks and the last four digits.
 *   Null when the source holds neither.
 * @property {number|null} [cmPacketId] Upload only. The packet's number in
 *   the CM Portal, once QuickSubmit has sent it there.
 * @property {SubmissionDocument[]|null} [documents] Upload only. The
 *   packet's files.
 */

/**
 * One file in a QuickSubmit packet.
 *
 * @typedef {Object} SubmissionDocument
 * @property {string|null} fileName Null when QuickSubmit sent none.
 * @property {number|null} fileSize In bytes.
 */

/**
 * The PropTypes check for one `CombinedHistorySubmission`. The table, the
 * cards, and the history that lists them all use it, so the row shape is
 * declared once, beside its typedef.
 *
 * @type {PropTypes.Requireable<CombinedHistorySubmission>}
 */
export const submissionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  benefitType: PropTypes.string,
  claimantId: PropTypes.string,
  cmPacketId: PropTypes.number,
  confirmationNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      fileSize: PropTypes.number,
    }),
  ),
  firstName: PropTypes.string,
  formType: PropTypes.string,
  lastName: PropTypes.string,
  maskedIdentifier: PropTypes.string,
  numberOfFiles: PropTypes.number,
  packet: PropTypes.bool,
  packetStatus: PropTypes.string,
  packetType: PropTypes.string,
  source: PropTypes.string,
  submittedDate: PropTypes.string,
  type: PropTypes.oneOf(Object.values(SUBMISSION_TYPES)),
  vbmsReceivedDate: PropTypes.string,
  vbmsStatus: PropTypes.string,
});

/**
 * The loaded history. `partial` is true when either source was unavailable
 * or cut short, so some rows may be missing. `loadStartedAt` is when the
 * route loader was entered, in `performance.now()` milliseconds, for the
 * load-to-render measurement the page reports.
 *
 * @typedef {{ submissions: CombinedHistorySubmission[], partial: boolean, loadStartedAt: number } | { error: true }} CombinedHistory
 */

/**
 * `getCombinedHistory` in `utilities/api.js`. A `claimantId` asks vets-api
 * for that one claimant's history. Null asks for the representative's own.
 *
 * @typedef {(query: { claimantId: string|null }, options: { signal?: AbortSignal, skip403Redirect?: boolean }) => Promise<Response|null>} GetCombinedHistory
 */

/**
 * @typedef {{ params: Record<string, string>, request: Request }} LoaderArgs
 */

/**
 * The `source` of a form submitted through the portal. Every other source
 * is a QuickSubmit channel.
 *
 * @type {string}
 */
const ONLINE_FORM_SOURCE = 'arp_form';

/**
 * The `meta.sources` statuses that leave rows out. vets-api caps neither
 * source by count, so every record in the window is sent unless a read
 * failed or stopped early. `quickSubmit` is `unavailable` when any step of
 * its read failed, and `partial` when the read ran out of time and kept
 * only the newest uploads. `QuickSubmit::SubmissionHistory` in vets-api
 * lists the steps and the time budget. `arpForm` is always `ok`, because a
 * failure there fails the whole request. Every source is checked anyway,
 * so a cut added on that side would still show the warning. `disabled` is
 * left out. It means a precondition such as the feature flag was not met,
 * a standing condition rather than an outage, so it shows no warning.
 *
 * @type {string[]}
 */
const INCOMPLETE_SOURCE_STATUSES = ['unavailable', 'partial'];

/**
 * @param {Object|undefined} meta A response's `meta`.
 * @returns {boolean} Whether any source left rows out. A response with no
 *   `meta` reads as complete.
 */
const isPartial = meta =>
  Object.values(meta?.sources ?? {}).some(status =>
    INCOMPLETE_SOURCE_STATUSES.includes(status),
  );

/**
 * @param {unknown} documents A row's `documents`, as the endpoint returns it.
 * @returns {SubmissionDocument[]|null} Each file, with its name when the
 *   name is text and its size when the size is a number, or null when the
 *   row has no list. A file without a name still counts, so the list matches
 *   what QuickSubmit sent. An entry that isn't a file is dropped, so a
 *   malformed list can't take the page down when the modal lists it.
 */
const readDocuments = documents =>
  Array.isArray(documents)
    ? documents
        .filter(
          document =>
            typeof document === 'object' &&
            document !== null &&
            !Array.isArray(document),
        )
        .map(({ fileName, fileSize }) => ({
          fileName:
            typeof fileName === 'string' && fileName !== '' ? fileName : null,
          fileSize: Number.isFinite(fileSize) ? fileSize : null,
        }))
    : null;

/**
 * @param {Omit<CombinedHistorySubmission, 'type'>} row A row as the endpoint
 *   returns it.
 * @returns {CombinedHistorySubmission} The row with its `type`, as the
 *   table labels it, and its packet number and files in the shapes the
 *   modal reads.
 */
const withType = row => ({
  ...row,
  type:
    row.source === ONLINE_FORM_SOURCE
      ? SUBMISSION_TYPES.ONLINE_FORM
      : SUBMISSION_TYPES.QUICK_SUBMIT,
  cmPacketId: Number.isInteger(row.cmPacketId) ? row.cmPacketId : null,
  documents: readDocuments(row.documents),
});

/**
 * Reads an ISO 8601 date or datetime the way the table shows it: with any
 * timezone dropped, so a UTC time near midnight stays on the day it names.
 * `parseISO` reads a date-only value as local midnight, where `new Date`
 * reads it as UTC midnight and would put a form ahead of a same-day upload.
 *
 * @param {string|null|undefined} isoDate
 * @returns {Date|null} The local date and time, or null for a missing or
 *   unreadable value.
 */
export const readDate = isoDate => {
  // Anything but a non-empty string reads as no date, so a bad value from an
  // API leaves its cell blank instead of taking the table down.
  if (typeof isoDate !== 'string' || !isoDate) return null;
  const date = parseISO(stripTimezoneFromIsoDate(isoDate));
  return isValid(date) ? date : null;
};

// Newest first, read the way the table shows the date, so a UTC time near
// midnight sorts on the day shown. A row without a readable date sorts last,
// so it can't scramble the order. Each date is read once, not on every
// comparison.
const newestFirst = submissions =>
  submissions
    .map(submission => ({
      submission,
      time: readDate(submission.submittedDate)?.getTime() ?? -Infinity,
    }))
    .sort((a, b) => {
      if (a.time === b.time) return 0;
      return a.time > b.time ? -1 : 1;
    })
    .map(({ submission }) => submission);

/**
 * @param {import('redux').Store} store
 * @returns {boolean} Whether the submission history update toggle is on now,
 *   for a caller that cannot wait, such as a route's `shouldRevalidate`.
 *   False while the toggles are still loading.
 */
export const isCombinedHistoryEnabledSync = store =>
  !!toggleValuesSelector(store.getState())[
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateSubmissionHis
  ];

// The submission history update toggle turns on the combined history table.
const isCombinedHistoryEnabled = async store => {
  await waitForToggles(store);
  return isCombinedHistoryEnabledSync(store);
};

/**
 * Builds a history route's `shouldRevalidate`. The combined history pages,
 * sorts, and keeps that state in the URL in the browser, so a navigation
 * that changes only the query must not run the loader again: each run
 * requests both sources, and the Quick Submit request pays an accreditation
 * lookup on the server. A navigation to another path, and Refresh (a
 * revalidation with the same URL), still revalidate. With the toggle off the
 * server-paged list drives its loader through the same query parameters, so
 * the default stands.
 *
 * @param {import('redux').Store} store Read for the feature toggles.
 * @returns {(args: { currentUrl: URL, nextUrl: URL, defaultShouldRevalidate: boolean }) => boolean}
 * @example
 * { path: 'submission-history', shouldRevalidate: shouldRevalidateHistory(store) }
 */
export const shouldRevalidateHistory =
  store =>
  ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
    if (!isCombinedHistoryEnabledSync(store)) return defaultShouldRevalidate;
    if (currentUrl.pathname !== nextUrl.pathname)
      return defaultShouldRevalidate;
    if (currentUrl.search === nextUrl.search) return defaultShouldRevalidate;
    return false;
  };

// `utilities/api.js` resolves null for an expired session while it sends the
// browser to sign-in. The route's sign-in redirection handles a 401 rejection.
const requireSession = response => {
  if (!response) throw new Response(null, { status: 401 });
  return response;
};

// A request that never got a usable response.
class RequestFailed extends Error {}

/**
 * Reads a response's JSON body. Two failures count as the request failing,
 * so the page shows its inline alert: the `NetworkError` from
 * `utilities/api.js`, for a request that never got a response, and the body
 * failing to read or parse, for example an HTML maintenance page. Any other
 * TypeError is a bug and still reaches the error boundary.
 *
 * @param {Promise<Response|null>} request A call to `utilities/api.js`.
 * @returns {Promise<Object>} The parsed body.
 */
const readJson = async request => {
  let response;
  try {
    response = await request;
  } catch (e) {
    throw e instanceof NetworkError ? new RequestFailed(e.message) : e;
  }
  const body = requireSession(response).json();
  return Promise.resolve(body).catch(e => {
    throw new RequestFailed(e.message);
  });
};

/**
 * @param {Object|null} body A parsed response body.
 * @param {string} key The key that holds the body's list.
 * @returns {Object[]} The list. A null list is empty. A body without one, for
 *   example `{ errors: [...] }`, counts as a failed request, so the page shows
 *   its alert rather than "No submissions found".
 */
const listFrom = (body, key) => {
  const list = body?.[key];
  if (Array.isArray(list)) return list;
  if (list === null) return [];
  throw new RequestFailed(`The response has no ${key} list`);
};

/**
 * Requests the combined history and sorts it newest first. vets-api has
 * already merged both sources, cut them to the past 60 days, and normalized
 * the rows.
 *
 * @param {{ getCombinedHistory: GetCombinedHistory }} api An API client
 *   shaped like `utilities/api.js`.
 * @param {string|null} claimantId Limits the rows to one claimant. vets-api
 *   does the limiting, as `getCombinedHistory` in `utilities/api.js`
 *   describes. An id vets-api never issued answers an empty list.
 * @param {{ signal?: AbortSignal, skip403Redirect?: boolean }} options The
 *   request's options, passed to the API client as they are.
 * @param {number} [loadStartedAt] When the route loader was entered, which
 *   the result carries. Defaults to now.
 * @returns {Promise<CombinedHistory>} The rows, with `partial` true when
 *   either source was unavailable or cut short, or `{ error: true }` when
 *   the request fails, so the page shows its inline alert.
 * @throws {Response} A 401 for an expired session. Any other unexpected
 *   error also rejects, for the route's error boundary.
 */
const fetchCombinedHistory = async (
  api,
  claimantId,
  options,
  loadStartedAt = performance.now(),
) => {
  try {
    const body = await readJson(
      api.getCombinedHistory({ claimantId }, options),
    );
    // A vets-api without the `claimant_id` parameter ignores it and answers
    // every claimant's rows, so a website build that deploys first would list
    // every claimant on a claimant's page. The filter keeps that page to one
    // claimant either way. It drops no row the parameter keeps, since every
    // row vets-api keeps for a claimant carries their id.
    const submissions = newestFirst(
      listFrom(body, 'data')
        .filter(row => !claimantId || row.claimantId === claimantId)
        .map(withType),
    );

    return { submissions, partial: isPartial(body.meta), loadStartedAt };
  } catch (e) {
    // A failed request becomes the inline alert. A 401 keeps the route's
    // sign-in redirect. Anything else is a bug, and the error boundary makes
    // it visible.
    if (e instanceof RequestFailed) return { error: true };
    if (!(e instanceof Response) || e.status === 401) throw e;
    return { error: true };
  }
};

/**
 * Marks a history request the loader returns unawaited as handled. The page
 * never reads it after a navigation away or a 403 on the claimant overview,
 * and an unread rejection, an abort's included, would be reported as
 * unhandled. The page still sees the rejection when it awaits the request.
 *
 * @template T
 * @param {Promise<T>} request
 * @returns {Promise<T>} The same request.
 */
const handOff = request => {
  request.catch(() => {});
  return request;
};

/**
 * A controller for the history request the loader returns unawaited. The
 * router aborts the route's own signal only while the loader runs, so once
 * the loader has returned, leaving the page would let the request run on.
 * The page aborts this one instead, and it follows the route's signal while
 * the loader is still running.
 *
 * @param {AbortSignal} [routeSignal] The route's `request.signal`.
 * @returns {AbortController}
 */
const historyController = routeSignal => {
  const controller = new AbortController();
  if (routeSignal?.aborted) {
    controller.abort();
  } else {
    routeSignal?.addEventListener('abort', () => controller.abort(), {
      once: true,
    });
  }
  return controller;
};

/**
 * Builds the route loader for the combined submission history.
 *
 * The loader reads the toggle before it calls the API, so a page whose toggle
 * is off never requests the history. It returns the history request without
 * awaiting it. The router renders nothing for a route until its loader
 * resolves, and the request reads two sources and can take several seconds,
 * so the page shows its loading indicator in the meantime. See
 * `fetchCombinedHistory` for what the request resolves to. The page cancels
 * the request with `abortCombinedHistory` when it no longer needs the
 * result, as when the reader leaves.
 *
 * @param {{ getCombinedHistory: GetCombinedHistory }} api An API client
 *   shaped like `utilities/api.js`.
 * @param {import('redux').Store} store Read for the feature toggles.
 * @returns {(args: LoaderArgs) => Promise<{ combinedHistory: Promise<CombinedHistory>, abortCombinedHistory: () => void } | { combinedHistoryDisabled: true }>}
 *   A loader whose request limits the history to `params.claimantId` on a
 *   route that has one. The request rejects with a 401 `Response` when the
 *   session has expired.
 * @example
 * SubmissionHistoryPage.loader = loadCombinedHistory(api, store);
 */
export const loadCombinedHistory =
  (api, store) =>
  async ({ params, request }) => {
    const loadStartedAt = performance.now();
    if (!(await isCombinedHistoryEnabled(store))) {
      return { combinedHistoryDisabled: true };
    }

    const controller = historyController(request.signal);
    return {
      combinedHistory: handOff(
        fetchCombinedHistory(
          api,
          params?.claimantId ?? null,
          { signal: controller.signal },
          loadStartedAt,
        ),
      ),
      abortCombinedHistory: () => controller.abort(),
    };
  };

/**
 * Reads the claimant's name from the claimant overview. A failed request
 * leaves the name empty rather than taking the history down with it. A 403
 * means the representative does not represent the claimant, so it rejects
 * with that `Response`, as does an expired session's 401.
 *
 * @param {{ getClaimantOverview: Function }} api An API client shaped like
 *   `utilities/api.js`.
 * @param {string} claimantId The claimant's portal ID.
 * @param {AbortSignal} [signal] Cancels the request.
 * @returns {Promise<{ firstName: string, lastName: string }>} The name, with
 *   empty strings for any part the overview does not give.
 */
const loadClaimantName = async (api, claimantId, signal) => {
  let body;
  try {
    body = await readJson(api.getClaimantOverview(claimantId, { signal }));
  } catch (e) {
    if (e instanceof Response && [401, 403].includes(e.status)) throw e;
    // Any other failed request, a request that never got a response, or an
    // unreadable body leaves the name blank. Anything else is a bug.
    const requestFailed = e instanceof RequestFailed || e instanceof Response;
    if (!requestFailed) throw e;
    return { firstName: '', lastName: '' };
  }
  const overview = body?.data ?? body ?? {};
  return {
    firstName: overview.firstName ?? overview.first_name ?? '',
    lastName: overview.lastName ?? overview.last_name ?? '',
  };
};

/**
 * Builds the loader for a claimant's submission history.
 *
 * With the submission history update toggle off, the loader redirects a URL
 * without a valid `sort` to the default sort, page size, and page number, then
 * loads that page of submissions. With the toggle on, the table sorts on the
 * client, so the loader skips the redirect and requests the combined history.
 * The combined history has no claimant name, so the loader reads it from the
 * claimant overview at the same time. It awaits the name, which is quick and
 * fills the page's heading, and returns the history request unawaited, as
 * `loadCombinedHistory` does.
 *
 * @param {{ getClaimantSubmissions: Function, getClaimantOverview: Function, getCombinedHistory: GetCombinedHistory }} api
 *   An API client shaped like `utilities/api.js`.
 * @param {import('redux').Store} store The portal's Redux store, read for the
 *   feature toggles.
 * @returns {(args: LoaderArgs) => Promise<{ claimant: Object, combinedHistory: Promise<CombinedHistory>, abortCombinedHistory: () => void } | { notRepresented: true } | { data: Object[], meta: Object, claimant: Object }>}
 *   A loader. Under either toggle, it resolves to `{ notRepresented: true }`
 *   for a malformed claimant ID before any request, as the QuickSubmit
 *   loader does, and rejects with a 401 `Response` when the session has
 *   expired. With the toggle on, it resolves to the claimant and the
 *   combined history request, with the function that cancels it, or to
 *   `{ notRepresented: true }` when the claimant overview answers 403,
 *   having cancelled the history request. With the toggle off, it throws a
 *   redirect `Response` for a URL without a valid sort, and otherwise
 *   resolves to the `{ data, meta, claimant }` response body.
 * @example
 * ClaimantSubmissionHistoryPage.loader = loadClaimantSubmissionHistory(
 *   api,
 *   store,
 * );
 */
export const loadClaimantSubmissionHistory =
  (api, store) =>
  async ({ params, request }) => {
    const loadStartedAt = performance.now();
    const { searchParams } = new URL(request.url);
    const sort = searchParams.get(SEARCH_PARAMS.SORT);
    const size = searchParams.get(SEARCH_PARAMS.SIZE);
    const number = searchParams.get(SEARCH_PARAMS.NUMBER);
    const identifier = params.claimantId;

    // A malformed id shows "Claimant not found", which says what happened. A
    // redirect to the dashboard would leave an authorized representative
    // there with no explanation.
    if (!CLAIMANT_ID_PATTERN.test(identifier)) {
      return { notRepresented: true };
    }

    if (await isCombinedHistoryEnabled(store)) {
      const controller = historyController(request.signal);
      // A 403 on the history must not redirect to the dashboard, or it
      // would race the claimant overview's "Claimant not found" below.
      const combinedHistory = handOff(
        fetchCombinedHistory(
          api,
          identifier,
          { signal: controller.signal, skip403Redirect: true },
          loadStartedAt,
        ),
      );
      try {
        const claimant = await loadClaimantName(
          api,
          identifier,
          request.signal,
        );
        return {
          claimant,
          combinedHistory,
          abortCombinedHistory: () => controller.abort(),
        };
      } catch (e) {
        // Nothing will read the history now.
        controller.abort();
        // The claimant overview answers 403 when the representative does not
        // represent the claimant. Show "Claimant not found", as the Overview
        // tab does, rather than the history.
        if (e instanceof Response && e.status === 403) {
          return { notRepresented: true };
        }
        throw e;
      }
    }

    if (!Object.values(SORT_BY).includes(sort)) {
      searchParams.set(SEARCH_PARAMS.SORT, SORT_BY.NEWEST);
      searchParams.set(SEARCH_PARAMS.SIZE, SUBMISSION_DEFAULTS.SIZE);
      searchParams.set(SEARCH_PARAMS.NUMBER, SUBMISSION_DEFAULTS.NUMBER);
      throw redirect(`?${searchParams}`);
    }

    const response = await api.getClaimantSubmissions(
      { identifier, sort, size, number },
      { signal: request.signal },
    );

    return requireSession(response).json();
  };
